'use client'

import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/hooks/useAuth'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useTranslation } from 'react-i18next'
import { SocialLoginSection } from '@/components/auth/SocialLoginSection'
import EmailVerificationMessage from '@/components/auth/EmailVerificationMessage'
import { supabase } from '@/integrations/supabase/client'
import { Eye, EyeOff } from 'lucide-react'
import { useFacilityBranding, useOrgVocab } from '@/contexts/SubdomainBrandingContext'
import BrandLogo from '@/components/common/BrandLogo'

export default function SignupPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isFacebookLoading, setIsFacebookLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showEmailVerification, setShowEmailVerification] = useState(false)
  const [isResendingVerification, setIsResendingVerification] = useState(false)

  const { user, signUp, signIn, signInWithGoogle, signInWithFacebook } = useAuth()
  const facilityBranding = useFacilityBranding()
  const vocab = useOrgVocab(facilityBranding.org_type)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  // Redirect if user is already authenticated
  useEffect(() => {
    if (user && !isLoading) {
      const redirectParam = searchParams.get('redirect')
      const targetPath = redirectParam ? decodeURIComponent(redirectParam) : '/'
      console.log('[SIGNUP] User already authenticated, redirecting to', targetPath)
      navigate(targetPath, { replace: true })
    }
  }, [user, navigate, searchParams, isLoading])

  // Pre-fill email from URL parameters
  useEffect(() => {
    const emailParam = searchParams.get('email')
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam))
    }
  }, [searchParams])

  // Extract name from email when email changes and name is empty
  useEffect(() => {
    if (email && !name) {
      const emailName = email.split('@')[0]
      const extractedName = emailName
        .split(/[._-]/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ')
      setName(extractedName)
    }
  }, [email, name])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    if (password !== confirmPassword) {
      setError(t('auth.signup.passwordsMismatch'))
      setIsLoading(false)
      return
    }

    if (password.length < 6) {
      setError(t('auth.signup.passwordTooShort'))
      setIsLoading(false)
      return
    }

    try {
      const { data, error } = await signUp(email, password, name)

      if (error) {
        console.log('Signup error details:', error)
        setError((error as any)?.message || 'Signup failed')
      } else if (data && (data as any).user) {
        const user = (data as any).user
        if (!user.email_confirmed_at) {
          // Email will be sent automatically by Supabase Auth Hook
          setShowEmailVerification(true)
        } else {
          // Route through the GUARD (navigate to '/'), not straight to the
          // target — the guard performs role-based routing AND consumes the
          // return path. Direct navigation here bypassed parent→/homeschool/parent.
          const redirectParam = searchParams.get('redirect')
          if (redirectParam) {
            const raw = decodeURIComponent(redirectParam)
            const safe = raw.startsWith('/') && !raw.startsWith('//')
            if (safe) { try { localStorage.setItem('sage_auth_return_path', raw) } catch { /* non-fatal */ } }
          }
          navigate('/', { replace: true })
        }
      } else if (data && (data as any).user === null && !error) {
        // This happens when user already exists but no error is thrown
        // Try silent login with the same credentials
        console.log('[SIGNUP] User already exists, attempting silent login...')
        const { data: signInData, error: signInError } = await signIn(email, password)

        if (!signInError && (signInData as any)?.user) {
          console.log('[SIGNUP] Silent login successful')
          const redirectParam = searchParams.get('redirect')
          if (redirectParam) {
            const raw = decodeURIComponent(redirectParam)
            const safe = raw.startsWith('/') && !raw.startsWith('//')
            if (safe) { try { localStorage.setItem('sage_auth_return_path', raw) } catch { /* non-fatal */ } }
          }
          navigate('/', { replace: true })
          return
        }

        setError('An account with this email already exists. Please sign in with your password.')
      }
    } catch (err) {
      console.error('Signup catch error:', err)
      setError(t('auth.signup.unexpectedError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true)
    setError('')

    try {
      const { error } = await signInWithGoogle()
      if (error) {
        setError((error as any)?.message || 'Google sign-in failed')
      }
    } catch {
      setError('Failed to sign in with Google')
    } finally {
      setIsGoogleLoading(false)
    }
  }

  const handleFacebookSignIn = async () => {
    setIsFacebookLoading(true)
    setError('')

    try {
      const { error } = await signInWithFacebook()
      if (error) {
        setError((error as any)?.message || 'Facebook sign-in failed')
      }
    } catch {
      setError('Failed to sign in with Facebook')
    } finally {
      setIsFacebookLoading(false)
    }
  }

  const handleResendVerification = async () => {
    setIsResendingVerification(true)
    try {
      // Try the standard Supabase auth resend first
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`
        }
      })

      if (error) {
        console.error('Resend verification error:', error)
        // If that fails, try calling our custom email function directly
        try {
          const response = await fetch('https://lpwjdezzaylhiuyyugqt.supabase.co/functions/v1/send-email', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
            },
            body: JSON.stringify({
              type: 'verification',
              to: email,
              userName: name,
              verificationUrl: `${window.location.origin}/auth/verify?token=${Date.now()}`
            })
          })

          if (response.ok) {
            alert('Verification email sent via custom service! Please check your inbox and spam folder.')
          } else {
            throw new Error('Custom email service failed')
          }
        } catch (customError) {
          console.error('Custom email error:', customError)
          alert(`Failed to resend verification email: ${error.message}. Please contact support.`)
        }
      } else {
        alert('Verification email sent! Please check your inbox and spam folder.')
      }
    } catch (error) {
      console.error('Resend verification error:', error)
      alert('Failed to resend verification email. Please try again.')
    } finally {
      setIsResendingVerification(false)
    }
  }


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0
    }
  }

  const accent = facilityBranding.is_branded ? (facilityBranding.secondary_color || '#d4a843') : '#4a7c5e'

  if (showEmailVerification) {
    return (
      <div style={{ minHeight: '100dvh', background: '#020617', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px', position: 'relative', overflow: 'hidden' }}>
        <div aria-hidden style={{ position: 'absolute', inset: 0, opacity: 0.12, background: `radial-gradient(ellipse 55% 40% at 50% 0%, ${accent} 0%, transparent 65%)`, pointerEvents: 'none' }} />
        <EmailVerificationMessage
          email={email}
          onResendVerification={handleResendVerification}
          isResending={isResendingVerification}
        />
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#020617', fontFamily: "'Inter', system-ui, sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 16px', position: 'relative', overflow: 'hidden' }}>
      {/* Radial glow — org-colored when branded, sage when SWA */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, opacity: 0.12, background: `radial-gradient(ellipse 55% 40% at 50% 0%, ${accent} 0%, transparent 65%)`, pointerEvents: 'none' }} />
      <div aria-hidden style={{ position: 'absolute', inset: 0, opacity: 0.05, backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)', backgroundSize: '28px 28px', pointerEvents: 'none' }} />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 580 }}
      >
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <motion.div
            variants={itemVariants}
            style={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}
          >
            {facilityBranding.is_branded && facilityBranding.logo_url ? (
              <BrandLogo src={facilityBranding.logo_url} alt={facilityBranding.display_name} style={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain' }} />
            ) : (
              <img src="/icons/blue-sage-logo.png" alt="Sage Logo" style={{ width: 80, height: 80, objectFit: 'contain' }} />
            )}
          </motion.div>

          <motion.h2 variants={itemVariants} style={{ fontSize: 'clamp(1.8rem, 5vw, 2.6rem)', fontWeight: 900, color: '#ffffff', margin: '0 0 10px', letterSpacing: '-0.02em', fontFamily: "'Playfair Display', Georgia, serif" }}>
            {facilityBranding.is_branded ? `Join ${facilityBranding.display_name}` : t('auth.signup.title')}
          </motion.h2>
          <motion.p variants={itemVariants} style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
            {facilityBranding.is_branded ? `Create your ${vocab.learnerPortalName || 'training'} account` : t('auth.signup.subtitle')}
          </motion.p>
        </div>

        <motion.div
          variants={itemVariants}
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 24, padding: '40px 36px', backdropFilter: 'blur(16px)', boxShadow: '0 30px 80px rgba(0,0,0,0.6)' }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-5 rounded-2xl font-bold mb-6"
                style={{ background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.28)', color: '#fca5a5' }}
              >
                {error}
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label htmlFor="name" style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>
                  {t('auth.signup.nameLabel')}
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder={t('auth.signup.namePlaceholder')}
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', borderRadius: 14, height: 52, padding: '0 18px', fontSize: 15 }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label htmlFor="email" style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>
                  {t('auth.signup.emailLabel')}
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder={t('auth.signup.emailPlaceholder')}
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', borderRadius: 14, height: 52, padding: '0 18px', fontSize: 15 }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label htmlFor="password" style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>
                  {t('auth.signup.passwordLabel')}
                </label>
                <div style={{ position: 'relative' }}>
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder={t('auth.signup.passwordPlaceholder')}
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', borderRadius: 14, height: 52, padding: '0 50px 0 18px', fontSize: 15, width: '100%' }}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword(v => !v)}
                    style={{ position: 'absolute', inset: 0, left: 'auto', right: 0, width: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)' }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label htmlFor="confirmPassword" style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>
                  {t('auth.signup.confirmPasswordLabel')}
                </label>
                <div style={{ position: 'relative' }}>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder={t('auth.signup.confirmPasswordPlaceholder')}
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', borderRadius: 14, height: 52, padding: '0 50px 0 18px', fontSize: 15, width: '100%' }}
                  />
                  <button
                    type="button"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowConfirmPassword(v => !v)}
                    style={{ position: 'absolute', inset: 0, left: 'auto', right: 0, width: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)' }}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ paddingTop: 8 }}>
              <Button
                type="submit"
                className="w-full font-black rounded-2xl"
                style={{ height: 52, fontSize: 16, background: `linear-gradient(135deg, ${accent}cc, ${accent})`, color: '#020617', border: 'none', boxShadow: `0 8px 28px ${accent}44` }}
                disabled={isLoading || isGoogleLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <LoadingSpinner size="sm" className="mr-3" />
                    {t('auth.signup.signingUp')}
                  </div>
                ) : (
                  t('auth.signup.submitButton')
                )}
              </Button>
            </div>

            {/* Social Login Section */}
            <div className="mt-8">
              <SocialLoginSection
                onGoogleSignIn={handleGoogleSignIn}
                onFacebookSignIn={handleFacebookSignIn}
                isGoogleLoading={isGoogleLoading}
                isFacebookLoading={isFacebookLoading}
                disabled={isLoading}
              />
            </div>

            <div style={{ marginTop: 28, textAlign: 'center' }}>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
                {t('auth.signup.hasAccount')}{' '}
                <Link
                  to="/auth/login"
                  onClick={() => {
                    const rp = (() => { try { return localStorage.getItem('sage_auth_return_path') } catch { return null } })()
                    if (rp) { try { sessionStorage.setItem('sage_signup_return_hop', rp) } catch { /* non-fatal */ } }
                  }}
                  style={{ color: accent, fontWeight: 700, textDecoration: 'none' }}
                >
                  {t('auth.signup.signInLink')}
                </Link>
              </p>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </div>
  )
}